import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Crown, CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 8;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const sessionId = params.get("session_id");

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setStatus("error"); return; }
    if (attempt >= MAX_POLLS) { setStatus("timeout"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        await refresh();
        setStatus("paid");
        return;
      }
      if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId, refresh]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="w-full max-w-md bg-white rounded-3xl border border-linen-3 p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-brand mx-auto mb-5 animate-spin" />
            <h1 className="font-serif text-3xl text-ink">Confirming your membership…</h1>
            <p className="text-ink-2 mt-3">One moment while we finalize your payment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <span className="w-16 h-16 rounded-full bg-calm flex items-center justify-center mx-auto mb-5">
              <Crown className="w-8 h-8 text-brand" />
            </span>
            <h1 className="font-serif text-3xl text-ink" data-testid="payment-success-title">Welcome to Aurea Plus</h1>
            <p className="text-ink-2 mt-3 leading-relaxed">
              Your membership is active. Unlimited conversations and every depth tool are now open to you.
            </p>
            <Link to="/app"><Button className="rounded-full bg-brand hover:bg-brand-hover text-linen mt-7 px-8 h-12" data-testid="payment-success-continue">Continue to Aurea</Button></Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-brand mx-auto mb-5" />
            <h1 className="font-serif text-3xl text-ink">Payment received</h1>
            <p className="text-ink-2 mt-3">Your membership will activate shortly. If it doesn't appear, refresh in a minute.</p>
            <Link to="/app"><Button className="rounded-full bg-brand hover:bg-brand-hover text-linen mt-7 px-8 h-12">Continue</Button></Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 text-accent mx-auto mb-5" />
            <h1 className="font-serif text-3xl text-ink">We couldn't confirm this</h1>
            <p className="text-ink-2 mt-3">No charge may have gone through. You can try again anytime.</p>
            <Link to="/app/plus"><Button variant="outline" className="rounded-full border-brand text-brand mt-7 px-8 h-12">Back to Aurea Plus</Button></Link>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="min-h-screen bg-linen flex items-center justify-center p-6" data-testid="payment-cancel-page">
    <div className="w-full max-w-md bg-white rounded-3xl border border-linen-3 p-10 text-center">
      <XCircle className="w-12 h-12 text-ink-mute mx-auto mb-5" />
      <h1 className="font-serif text-3xl text-ink">Checkout cancelled</h1>
      <p className="text-ink-2 mt-3">No worries — nothing was charged. Aurea Plus is here whenever you're ready.</p>
      <div className="flex gap-3 justify-center mt-7">
        <Link to="/app/plus"><Button className="rounded-full bg-brand hover:bg-brand-hover text-linen px-6 h-12">View Aurea Plus</Button></Link>
        <Link to="/app"><Button variant="outline" className="rounded-full border-brand text-brand px-6 h-12">Back to app</Button></Link>
      </div>
    </div>
  </div>
);
